<script lang="ts">
export default {
  name: 'ModernColumnFilter',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { Columns3 } from 'lucide-vue-next';

interface Field {
  value: string;
  text: string;
  stack: string;
  comparer?: (direction: 'ascending' | 'descending') => (a: any, b: any) => number;
}

const props = withDefaults(
  defineProps<{
    defaultFields: string[];
    appliedFields?: string[];
    fields: Field[];
  }>(),
  {
    appliedFields: undefined,
  },
);

const emit = defineEmits<{
  change: [fields: string[]];
}>();

// State
const menu = ref(false);
const searchQuery = ref('');
const localSelectedFields = ref<string[]>([]);
const dirty = ref(false);
const activeCategory = ref('');

// Computed
const selectedFields = computed(() => {
  return props.appliedFields || props.defaultFields || [];
});

const selectedCount = computed(() => {
  // When menu is open, show the current selection count
  // Otherwise show the applied fields count
  return menu.value ? localSelectedFields.value.length : selectedFields.value.length;
});

const localSelectedCount = computed(() => {
  return localSelectedFields.value.length;
});

const stacks = computed(() => {
  const uniqueStacks = [...new Set(props.fields.map((f) => f.stack))];
  return ['All Columns', ...uniqueStacks];
});

const activeFields = computed(() => {
  if (!activeCategory.value) return [];
  if (activeCategory.value === 'All Columns') {
    return props.fields;
  }
  return props.fields.filter((f) => f.stack === activeCategory.value);
});

const filteredActiveFields = computed(() => {
  if (!searchQuery.value) return activeFields.value;
  const query = searchQuery.value.toLowerCase();
  return activeFields.value.filter((field) => field.text.toLowerCase().includes(query));
});

const isCategoryAllSelected = computed(() => {
  if (activeFields.value.length === 0) return false;
  return activeFields.value.every((field) => localSelectedFields.value.includes(field.value));
});

const isCategoryIndeterminate = computed(() => {
  if (activeFields.value.length === 0) return false;
  const selectedInCategory = activeFields.value.filter((field) => localSelectedFields.value.includes(field.value));
  return selectedInCategory.length > 0 && selectedInCategory.length < activeFields.value.length;
});

onMounted(() => {
    localSelectedFields.value = [...selectedFields.value];
});

// Watch
watch(menu, (val) => {
  if (val) {
    // When opening, sync local state with applied fields
    localSelectedFields.value = [...selectedFields.value];
    searchQuery.value = '';
    activeCategory.value = stacks.value[0] || '';
  }
});

watch(localSelectedFields, () => {
  dirty.value = true;
});

function resetToDefaults() {
  localSelectedFields.value = [...props.defaultFields];
}

function toggleCategorySelectAll() {
  const categoryFieldValues = activeFields.value.map((f) => f.value);
  if (isCategoryAllSelected.value) {
    // Remove all category fields from selection
    localSelectedFields.value = localSelectedFields.value.filter((val) => !categoryFieldValues.includes(val));
  } else {
    // Add all category fields to selection
    const newValues = categoryFieldValues.filter((val) => !localSelectedFields.value.includes(val));
    localSelectedFields.value = [...localSelectedFields.value, ...newValues];
  }
}

function getCategorySelectionCount(category: string) {
  const categoryFields = category === 'All Columns' 
    ? props.fields 
    : props.fields.filter((f) => f.stack === category);
  return categoryFields.filter((field) => localSelectedFields.value.includes(field.value)).length;
}

function cancel() {
  localSelectedFields.value = [...selectedFields.value];
  menu.value = false;
}

function apply() {
  if (dirty.value && localSelectedFields.value.length > 0) {
    emit('change', localSelectedFields.value);
  }
  menu.value = false;
}
</script>

<template>
  <div class="modern-filter-button tw:p-1">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      location="bottom"
      origin="left top"
      transition="slide-x-transition">
      <template #activator="{ props }">
        <div
          class="tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-3 tw:py-1.5 tw:hover:bg-gray-50 tw:select-none"
          v-bind="props"
          data-testid="column-filter-activator">
          <Columns3  class="tw:h-4 tw:w-4 tw:stroke-primary" />
          <span class="tw:text-sm tw:font-medium">Columns</span>
          <span class="tw:badge tw:badge-sm tw:bg-base-200 tw:border-gray-400">{{ selectedCount }}</span>
        </div>
      </template>

      <div
        class="mint-filter-popup tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:shadow-xl">
        <!-- Header -->
        <div class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-gray-200 tw:p-4">
          <h3 class="tw:text-lg tw:font-semibold">Columns</h3>
          <div class="tw:flex tw:items-center tw:gap-4">
            <span class="tw:text-sm tw:text-gray-600">{{ localSelectedCount }} columns selected</span>
            <a @click="resetToDefaults" class="tw:cursor-pointer tw:text-sm tw:text-blue-600 tw:hover:underline" data-testid="column-filter-reset">
              Reset
            </a>
          </div>
        </div>

        <div class="tw:flex tw:h-[500px]">
          <!-- Left sidebar -->
          <div class="tw:w-48 tw:border-r tw:border-gray-200 tw:bg-gray-50 tw:select-none">
            <div class="tw:p-3">
              <div
                v-for="stack in stacks"
                data-testid="column-filter-category"
                :key="stack"
                @click="activeCategory = stack"
                class="tw:mb-1 tw:flex tw:cursor-pointer tw:cursor- tw:items-center tw:justify-between tw:rounded-md tw:px-3 tw:py-2 tw:text-sm"
                :class="[
                  activeCategory === stack
                    ? 'tw:bg-blue-100 tw:font-medium tw:text-blue-700'
                    : 'tw:text-gray-700 tw:hover:bg-gray-100',
                  stack !== 'All Columns' ? 'tw:ml-4' : ''
                ]">
                <span>{{ stack }}</span>
                <span
                  v-if="getCategorySelectionCount(stack) > 0"
                  class="tw:inline-flex tw:h-5 tw:min-w-[20px] tw:items-center tw:justify-center tw:rounded-full tw:px-1.5 tw:text-xs tw:font-medium tw:bg-base-200 tw:border-gray-400 tw:border-solid tw:border"
                  >
                  {{ getCategorySelectionCount(stack) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Right content -->
          <div class="tw:flex tw:flex-1 tw:flex-col">
            <!-- Search bar -->
            <div class="tw:border-b tw:border-gray-200 tw:p-4">
              <div class="tw:relative">
                <svg
                  class="tw:absolute tw:left-3 tw:top-2.5 tw:h-4 tw:w-4 tw:text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Search categories..."
                  class="tw:w-full tw:rounded-md tw:border-solid tw:border tw:border-gray-300 tw:bg-gray-50 tw:py-2 tw:pl-9 tw:pr-3 tw:text-sm tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-blue-500"
                  data-testid="column-filter-search" />
              </div>
            </div>

            <!-- Content area -->
            <div class="tw:flex-1 tw:overflow-y-auto tw:p-4">
              <!-- Select all -->
              <label class="tw:mb-4 tw:flex tw:cursor-pointer tw:items-center tw:gap-3">
                <input
                  type="checkbox"
                  :checked="isCategoryAllSelected"
                  :indeterminate="isCategoryIndeterminate"
                  @change="toggleCategorySelectAll"
                  class="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300" />
                <span class="tw:text-sm tw:text-gray-700">Select all</span>
              </label>

              <!-- Fields list -->
              <label
                v-for="field in filteredActiveFields"
                :key="field.value"
                class="tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:p-2 tw:hover:bg-gray-50">
                <input
                  type="checkbox"
                  v-model="localSelectedFields"
                  :value="field.value"
                  class="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300" />
                <div class="tw:flex tw:flex-1 tw:items-center tw:gap-2">
                  <span class="tw:text-sm tw:text-gray-700">{{ field.text }}</span>
                </div>
              </label>

              <!-- No results -->
              <div v-if="filteredActiveFields.length === 0" class="tw:py-8 tw:text-center tw:text-gray-500">
                No fields match your search
              </div>
            </div>

            <!-- Footer -->
            <div class="tw:flex tw:justify-end tw:gap-3 tw:border-t tw:border-gray-200 tw:p-4">
              <button
                @click="cancel"
                class="tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-700 tw:hover:bg-gray-50">
                Cancel
              </button>
              <button
                @click="apply"
                class="tw:rounded-md tw:bg-primary tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-white tw:opacity-90 tw:hover:opacity-100 tw:transition-colors">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style scoped>
.modern-filter-button {
  display: inline-block;
}

.mint-filter-popup {
  width: 650px;
}

/* Customize checkbox appearance */
input[type='checkbox'] {
  cursor: pointer;
}

input[type='checkbox']:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

input[type='checkbox']:indeterminate {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 16 16'%3e%3cpath d='M5 8h6'/%3e%3c/svg%3e");
  background-color: #3b82f6;
  border-color: #3b82f6;
}
</style>
